import React from 'react'
import { Card } from 'antd'
import { format, parseISO } from 'date-fns'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { IEnergyScoreForecastPoint } from '@vacationtracker/shared/types/user-insight'
import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'

interface IForecastChartProps {
  forecast: IEnergyScoreForecastPoint[]
}

const VIEWBOX_W = 600
const VIEWBOX_H = 160
const PAD_X = 36
const PAD_TOP = 12
const PAD_BOTTOM = 24

const ForecastChart: React.FC<IForecastChartProps> = ({ forecast }) => {
  if (forecast.length < 2) {
    return null
  }

  const drawWidth = VIEWBOX_W - PAD_X * 2
  const drawHeight = VIEWBOX_H - PAD_TOP - PAD_BOTTOM
  const stepX = drawWidth / (forecast.length - 1)

  const points = forecast.map((p, i) => {
    const clamped = Math.max(0, Math.min(100, p.score))
    return {
      x: PAD_X + i * stepX,
      y: PAD_TOP + ((100 - clamped) / 100) * drawHeight,
      score: p.score,
      date: p.date,
    }
  })

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')

  const baselineY = PAD_TOP + drawHeight
  const lastPoint = points[points.length - 1]
  const firstPoint = points[0]
  const areaPath = `${linePath} L ${lastPoint.x.toFixed(1)} ${baselineY.toFixed(1)} L ${firstPoint.x.toFixed(1)} ${baselineY.toFixed(1)} Z`

  const endpointMode = getEnergyMode(forecast[forecast.length - 1].score)

  const gridValues = [0, 25, 50, 75, 100]

  return (
    <Card size="small" className="te-forecast">
      <div className="te-forecast__header">
        <div className="te-forecast__title">
          <IntlMessages id="teamEnergy.forecast.title" />
        </div>
        <div className="te-forecast__subtitle">
          <IntlMessages id="teamEnergy.forecast.subtitle" values={{ weeks: forecast.length }} />
        </div>
      </div>
      <svg className={`te-forecast__chart te-forecast__chart--${endpointMode}`} viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`} role="img">
        {gridValues.map((v) => {
          const y = PAD_TOP + ((100 - v) / 100) * drawHeight
          return (
            <g key={v}>
              <line className="te-forecast__grid-line" x1={PAD_X} y1={y} x2={VIEWBOX_W - PAD_X} y2={y} />
              <text className="te-forecast__grid-label" x={PAD_X - 6} y={y + 4} textAnchor="end">
                {v}
              </text>
            </g>
          )
        })}
        <path d={areaPath} fill="currentColor" fillOpacity={0.18} stroke="none" />
        <path d={linePath} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={`p-${i}`} cx={p.x} cy={p.y} r={2} fill="currentColor">
            <title>{`${p.date}: ${p.score}`}</title>
          </circle>
        ))}
        {points.map((p, i) => (
          <text key={`l-${i}`} className="te-forecast__axis-label" x={p.x} y={VIEWBOX_H - 5} textAnchor="middle">
            {format(parseISO(p.date), 'MMM d')}
          </text>
        ))}
      </svg>
    </Card>
  )
}

export default ForecastChart
